<template>
  <div class="tree-container">
    <div style="width: 100%" class="d-flex align-center">
      <v-text-field
        v-model="searchTerm"
        :label="$t('nodeCompare.compareTree.searchLabel')"
        class="ml-2"
        append-icon="search"
        clearable
      />

      <v-btn
        @click="() => (expandFilter = !expandFilter)"
        outlined
        small
        class="mx-2"
        :color="isFiltering ? 'primary' : ''"
      >
        {{ $t("nodeCompare.compareTree.filterOptions.title") }}
        <v-icon v-if="isFiltering" right @click.stop="clearFilters()">
          mdi-close
        </v-icon>
        <v-icon right>
          {{ expandFilter ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
      </v-btn>
    </div>
    <div v-if="expandFilter" class="ml-4 mr-4 mb-2 mt-2">
      <v-autocomplete
        v-model="localFilterOptions.applications"
        :items="filterApplications"
        multiple
        item-text="name"
        item-value="applicationId"
        :label="$t('nodeCompare.compareTree.filterOptions.applications')"
        prepend-icon="mdi-apps"
        dense
      >
        <template v-slot:selection="data">
          <v-chip v-text="data.item.name" small />
        </template>

        <template v-slot:item="data">
          <v-list-item-content>
            <v-list-item-title v-html="data.item.name" />
          </v-list-item-content>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="localFilterOptions.nodeTypes"
        :items="Object.values(nodeTypes)"
        multiple
        :label="$t('nodeCompare.compareTree.filterOptions.nodeTypes')"
        prepend-icon="mdi-connection"
        dense
      >
        <template v-slot:item="data">
          <v-list-item-content>
            <v-list-item-title v-html="$t('tag.nodeTypes.' + data.item)" />
          </v-list-item-content>
        </template>

        <template v-slot:selection="data">
          <v-chip small>{{ $t("tag.nodeTypes." + data.item) }}</v-chip>
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="localFilterOptions.decoders"
        :items="decoders"
        multiple
        item-text="name"
        item-value="key"
        :label="$t('nodeCompare.compareTree.filterOptions.decoders')"
        prepend-icon="mdi-code-tags"
        dense
      >
        <template v-slot:item="data">
          <v-list-item-content>
            <v-list-item-title v-html="data.item.name" />
          </v-list-item-content>
        </template>

        <template v-slot:selection="data">
          <v-chip v-text="data.item.name" small />
        </template>
      </v-autocomplete>
      <v-divider />
    </div>

    <div v-for="(tag, i) in tree" :key="tag.deveui + i">
      <v-row
        :class="`d-flex justify-left align-center my-1 mx-0 flex-nowrap ${
          tag.keys.length > 0 ? 'tree-item-hover' : ''
        } ${
          tag.keys.filter((d) => d.selected).length ?? 0 > 0
            ? 'font-weight-medium'
            : ''
        }`"
        :style="`padding-left: 1 rem`"
        @click="() => toggleExpand(tag)"
      >
        <v-icon
          :color="tag.keys.length > 0 ? 'text' : 'grey lighten-1'"
          class="pl-2"
          style="cursor: pointer"
          >{{
            tag.expanded && tag.keys.length > 0
              ? "mdi-chevron-up"
              : "mdi-chevron-down"
          }}</v-icon
        >
        <span
          :class="`pl-2 ${tag.keys.length > 0 ? '' : 'grey--text'}`"
          style="
            cursor: pointer;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            display: block;
            font-size: 0.875rem;
          "
          >{{ tag.name }}</span
        >
      </v-row>

      <div v-if="tag.expanded">
        <div
          v-for="(key, i) in tag.keys"
          class="pl-10 tree-item-hover"
          style="text-align: left"
          :key="key.key + i"
          @click="() => toggleSelect(key)"
        >
          <v-icon v-if="!key.selected"> mdi-checkbox-blank-outline </v-icon>
          <v-icon v-else color="primary"> mdi-checkbox-marked </v-icon>
          <span class="pl-2" style="font-size: 0.875rem">{{ key.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions, mapMutations } from "vuex";
import TagRepository from "@/api/repositories/tagRepository";
import CsharpEnum from "../../_helpers/CsharpEnum";
import { FEATURES } from "../../_helpers/RestrictHelper";
import { isEqual } from "lodash-es";

export default {
  name: "CompareTree",

  props: {
    filterOptions: {
      default: undefined,
      type: Object,
    },
  },

  data() {
    return {
      tags: [],
      expandedTags: [],
      expandFilter: false,
      searchTerm: "",
      filterApplications: [],
      filterDecoders: [],
    };
  },

  model: { prop: "filterOptions" },

  computed: {
    ...mapState("compare", ["selectedNodes", "unfilteredSelectedNodes"]),
    ...mapState("tag", ["decoders"]),

    localFilterOptions: {
      get() {
        return this.filterOptions;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    tree() {
      const t = this.tags
        .filter((x) => {
          if (
            this.localFilterOptions?.applications.length > 0 &&
            this.localFilterOptions.applications.find(
              (y) => y === x.applicationId
            ) === undefined
          )
            return false;

          if (
            this.localFilterOptions?.nodeTypes.length > 0 &&
            this.localFilterOptions.nodeTypes.find((y) => y === x.nodeType) ===
              undefined
          )
            return false;

          if (
            this.localFilterOptions?.decoders.length > 0 &&
            this.localFilterOptions.decoders.find((y) => y === x.decoder) ===
              undefined
          )
            return false;

          return true;
        })
        .map((x) => {
          return {
            deveui: x.deveui,
            keys: x.sensors
              .map((y) => {
                return {
                  deveui: x.deveui,
                  key: y.key,
                  label: y.label,
                  selected:
                    this.unfilteredSelectedNodes.find(
                      (z) => z.deveui == x.deveui && z.key == y.key
                    ) !== undefined,
                };
              })
              .sort((a, b) => a.label < b.label),
            name: x.name,
            expanded:
              this.expandedTags.find((y) => x.deveui == y) !== undefined,
          };
        })
        .filter((x) => {
          if (
            this.searchTerm === "" ||
            this.searchTerm === undefined ||
            this.searchTerm === null
          )
            return true;

          if (
            x.deveui
              .toLocaleLowerCase()
              .includes(this.searchTerm.toLocaleLowerCase()) ||
            x.name
              .toLocaleLowerCase()
              .includes(this.searchTerm.toLocaleLowerCase())
          )
            return true;

          for (var sensor of x.keys)
            if (
              sensor.key
                .toLocaleLowerCase()
                .includes(this.searchTerm.toLocaleLowerCase()) ||
              sensor.label
                .toLocaleLowerCase()
                .includes(this.searchTerm.toLocaleLowerCase())
            )
              return true;

          return false;
        })
        .sort((a, b) => a.applicationId < b.applicationId);

      const filteredNodes = this.unfilteredSelectedNodes.filter(
        (x) =>
          t.find(
            (y) => y.deveui === x.deveui && y.keys.some((z) => z.key === x.key)
          ) !== undefined
      );
      if (isEqual(this.selectedNodes, filteredNodes) === false)
        this.SET_SELECTED_NODES(filteredNodes);
      return t;
    },

    isFiltering() {
      return (
        this.localFilterOptions?.applications.length > 0 ||
        this.localFilterOptions?.nodeTypes.length > 0 ||
        this.localFilterOptions?.decoders.length > 0
      );
    },

    nodeTypes() {
      let types = CsharpEnum.NodeTypes;
      if (!this.canAccessFeature(FEATURES.OPC)) delete types.OPC;
      return types;
    },
  },

  methods: {
    ...mapActions("tag", ["getDecoders"]),
    ...mapActions("companies", ["getCompanyApplications"]),
    ...mapMutations("compare", ["TOGGLE_NODE", "SET_SELECTED_NODES"]),

    clearFilters() {
      this.localFilterOptions.applications = [];
      this.localFilterOptions.nodeTypes = [];
      this.localFilterOptions.decoders = [];
    },

    toggleExpand(tag) {
      const idx = this.expandedTags.findIndex((d) => d == tag.deveui);
      idx !== -1
        ? this.expandedTags.splice(idx, 1)
        : this.expandedTags.push(tag.deveui);
    },

    toggleSelect(key) {
      this.TOGGLE_NODE(key);
    },
  },

  async created() {
    this.tags = (await TagRepository.getAllNodes()).data ?? [];
    this.filterApplications = (await this.getCompanyApplications()) ?? [];
    await this.getDecoders();

    for (var node of this.unfilteredSelectedNodes)
      if (this.expandedTags.find((y) => node.deveui == y) === undefined)
        this.expandedTags.push(node.deveui);
  },

  watch: {
    filterOptions: {
      // Shallow watcher so modifying the arrays inside filterOptions wont trigger. Only triggers when filterOptions is set =>
      // When a new view is loaded or compareSensors has been updated (klicked a new sensor in Tag list)
      handler() {
        const expanded = [];
        for (var node of this.unfilteredSelectedNodes)
          if (expanded.find((y) => node.deveui == y) === undefined)
            expanded.push(node.deveui);

        this.expandedTags = expanded;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/styles.sass";
.tree-container {
  max-width: 350px;
  width: 100%;
  height: 100%;

  flex-shrink: 0;
  min-width: 0;
}

.tree-item-hover:hover {
  font-weight: bold !important;
  cursor: pointer;
}
</style>
